<style>
.ProseMirror {
  min-height: 220px;
}
.ProseMirror [data-placeholder],
.ProseMirror span.placeholder {
  background: rgba(255, 235, 59, 0.25);
  border-radius: 4px;
  padding: 0 0.15rem;
}
</style>
<template>
  <div>
    <div class="mb-3" v-if="label">
      <h3>{{ label }}</h3>
    </div>
    <template v-if="showSeminarPlaceholders">
      <h4 class="mb-2">Textbausteine</h4>
      <div class="mb-3 d-flex flex-wrap" style="gap: 4px">
        <v-btn small outlined v-for="ph in this.placeholders" :key="ph.token" @click="insertPlaceholder(ph.token)">
          {{ ph.label }}
        </v-btn>
      </div>
    </template>
    <tiptap-vuetify
      v-model="textInput"
      :extensions="extensions"
      minHeight="220"
      @init="onTipTapEditorInit"
      @blur="blurFunction"
      @input="inputFunction"
      :disabled="disabled"
    />
    <div class="mx-2 mt-3 mb-2" v-if="errorMessages.length > 0">
      <div class="v-messages error--text theme--light" role="alert">
        <div class="v-messages__wrapper">
          <div class="v-messages__message" v-for="(errorMessage, i) in errorMessages" :key="i">
            {{ errorMessage }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  TiptapVuetify,
  Bold,
  Italic,
  Strike,
  Underline,
  Link,
  BulletList,
  OrderedList,
  ListItem,
  HardBreak,
  Image,
  Heading
} from 'tiptap-vuetify';
import ImageFileSelector from './TipTapEditor/ImageFileSelector';
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'

const placeholderKey = new PluginKey('placeholder-highlighter')

function createPlaceholderPlugin () {
  const makeDecorations = (doc) => {
    const decorations = []
    doc.descendants((node, pos) => {
      if (!node.isText) return
      const text = node.text || ''
      const re = /##[A-Z0-9_]+##/g
      let m
      while ((m = re.exec(text)) !== null) {
        const from = pos + m.index
        const to = from + m[0].length
        decorations.push(
          Decoration.inline(from, to, { class: 'placeholder', 'data-token': m[0] })
        )
      }
    })
    return DecorationSet.create(doc, decorations)
  }

  return new Plugin({
    key: placeholderKey,
    state: {
      init: (_, { doc }) => makeDecorations(doc),
      apply: (tr, old, _oldState, newState) => (tr.docChanged ? makeDecorations(newState.doc) : old),
    },
    props: {
      decorations (state) {
        return placeholderKey.getState(state)
      },
    },
  })
}

export default {
  components: {
    TiptapVuetify
  },
  props: {
    label: {
      type: String,
      required: false,
      default: () => ''
    },
    value: String,
    required: {
      type: Boolean,
      required: false,
      default: () => false
    },
    errorMessages: {
      type: Array,
      required: false,
      default: () => []
    },
    blurFunction: {
      type: Function,
      required: false,
      default: () => {}
    },
    inputFunction: {
      type: Function,
      required: false,
      default: () => {}
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false
    },
    simple: {
      type: Boolean,
      required: false,
      default: () => false
    },
    showSeminarPlaceholders: {
      type: Boolean,
      required: false,
      default: () => false
    }
  },
  data() {
    return {
      tipTapEditor: null,
      placeholders: [
        { label: 'Seminar-Name', token: '##SEMINAR_NAME##' },
        { label: 'Seminar-Nummer', token: '##SEMINAR_NUMBER##' },
        { label: 'Seminar-Start', token: '##SEMINAR_START##' },
        { label: 'Seminar-Ende', token: '##SEMINAR_END##' },
        { label: 'Seminar-Gebühr', token: '##SEMINAR_FEE##' },
        { label: 'Teilnehmer/in Name', token: '##PARTICIPANT_NAME##' },
      ]
    };
  },
  computed: {
    textInput: {
      get: function () {
        return this.value;
      },
      set: function (newValue) {
        if (typeof newValue !== 'undefined' && newValue !== null && !newValue.replace(/<[^>]*>?/gm, '')) {
          // new value is empty, so set it to null.
          newValue = null;
        }
        this.$emit('input', newValue);
      }
    },
    extensions() {
      const extArray = [Bold, Italic, Strike, Underline, HardBreak];

      if (this.simple === false) {
        extArray.push(
          [
            Heading,
            {
              options: {
                levels: [2, 3]
              }
            }
          ],
          ListItem,
          BulletList,
          OrderedList,
          Link,
          [
            Image,
            {
              options: {
                imageSourcesOverride: true,
                imageSources: [{ component: ImageFileSelector, name: 'Bild-Auswahl' }]
              }
            }
          ]
        );
      }
      return extArray;
    }
  },
  methods: {
    onTipTapEditorInit(editorWrapper) {
      this.tipTapEditor = editorWrapper
      const editor = editorWrapper && editorWrapper.editor
      if (editor && !this._placeholderPluginAdded) {
        editor.registerPlugin(createPlaceholderPlugin())
        this._placeholderPluginAdded = true
      }
    },
    getEditor() {
      return this.tipTapEditor && this.tipTapEditor.editor ? this.tipTapEditor.editor : null;
    },

    insertPlaceholder(token) {
      const editor = this.getEditor();
      if (!editor || this.disabled) return;

      editor.focus();

      const { state, view } = editor;
      const { from, to } = state.selection;

      const tr = state.tr.insertText(`${token} `, from, to);
      view.dispatch(tr);

      view.focus();
    }
  },
  watch: {
    value(newValue) {
      this.textInput = newValue;
    }
  },
  created() {
    this.textInput = this.value;
  }
};
</script>
